import { inject, Injectable} from "@angular/core";
import { BehaviorSubject, combineLatest, filter, Subject, takeUntil } from "rxjs";
import { ISelectedPlan, LoggedInUserSettings } from "./settings";
import { UserClaims } from "src/app/model/okta/userClaims";
import { ADPResourceService } from "digital-core";
import { NavigationEnd, Router } from "@angular/router";


@Injectable({
  providedIn: 'root',
})
export class ChatButtonService {
  private loggedInUserSettings = inject(LoggedInUserSettings);
  private router = inject(Router);
  private resourceService = inject(ADPResourceService);

  private destroy$ = new Subject<boolean>();

  private isChatButtonHidden = new BehaviorSubject<boolean>(false);
  isChatButtonHidden$ = this.isChatButtonHidden.asObservable();

  private isChatButtonAvailable = new BehaviorSubject<boolean>(false);
  isChatButtonAvailable$ = this.isChatButtonAvailable.asObservable();
  
  private buttonVisibility = new BehaviorSubject<boolean>(false);
  buttonVisibility$ = this.buttonVisibility.asObservable();

  private asappChatInitialized = new BehaviorSubject<boolean>(false);
  asappChatInitialized$ = this.asappChatInitialized.asObservable();

  private miawChatInitialized = new BehaviorSubject<boolean>(false);
  miawChatInitialized$ = this.miawChatInitialized.asObservable();

  private asappChatVisible = new BehaviorSubject<boolean>(false);
  asappChatVisible$ = this.asappChatVisible.asObservable();

  private miawChatVisible = new BehaviorSubject<boolean>(false);
  miawChatVisible$ = this.miawChatVisible.asObservable();

  private hiddenRoutes: string[] = [];
  private unauthenticatedChatEnabled = false;

  setChatButtonVisibility(isVisible: boolean): void {
    this.buttonVisibility.next(isVisible);
  }

  setAsappChatInitialized(isInitialized: boolean): void {
    this.asappChatInitialized.next(isInitialized);
  }

  enableForUnauthenticatedChat(): void {
    this.unauthenticatedChatEnabled = true;
    this.isChatButtonAvailable.next(true);
    this.setChatButtonVisibility(true);
  }

  setMiawChatInitialized(isInitialized: boolean): void {
    this.miawChatInitialized.next(isInitialized);
  }

  setAsappChatVisibility(isVisible: boolean): void {
    this.asappChatVisible.next(isVisible);
  }

  setMiawChatVisibility(isVisible: boolean): void {
    this.miawChatVisible.next(isVisible);
  }

  setChatButtonHidden(isHidden: boolean): void {
    this.isChatButtonHidden.next(isHidden);
  }

  isMiawChatVisible(): boolean {
    return this.miawChatVisible.getValue();
  }

  isAsappChatVisible(): boolean {
    return this.asappChatVisible.getValue();
  }

  destroy(): void {
    this.hide()
    this.destroy$.next(true);
  }

  hide(): void {
    this.unauthenticatedChatEnabled = false;
    this.setChatButtonVisibility(false);
    this.isChatButtonAvailable.next(false);
  }

  /**
   * Checks footer position and updates chat button positioning mode
   */
  checkFooterPosition(): void {
    const footerElement = document.querySelector('.footer-container') as HTMLElement;
    const chatButtonElement = document.querySelector('#pgh-floating-chat-button') as HTMLElement;
    
    if (!footerElement || !chatButtonElement) {
      //console.warn('Footer or chat button element not found yet');
      return;
    }

    const footerRect = footerElement.getBoundingClientRect();
    const buttonRect = chatButtonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    
    // Calculate how much of the footer is visible
    const footerVisibleHeight = Math.min(footerRect.bottom, viewportHeight) - Math.max(footerRect.top, 0);
    
    if (footerVisibleHeight > 0) {
      // Footer is visible - position button dynamically above footer
      const spaceAboveFooter = viewportHeight - footerRect.top;
      const factor = (window.innerWidth || document.documentElement.clientWidth) < 768 ? 0.25 : 0.5;
      const middlePosition = footerVisibleHeight + ((spaceAboveFooter - footerVisibleHeight) / 2) - (buttonRect.height * factor);
      if(middlePosition < 0) {
        chatButtonElement.style.bottom = '';
        return;
      }
      chatButtonElement.style.bottom = `${middlePosition}px`;
      //console.log(`Footer visible (${footerVisibleHeight}px), positioning button at ${middlePosition}px from bottom`);
    } else {
      // Footer not visible - clear inline style to use CSS default
      chatButtonElement.style.bottom = '';
      //console.log('Footer not visible, using default positioning');
    }
  }

  initialize() {
    // Split on ',' then drop empty entries — a blank CMS value otherwise produces
    // hiddenRoutes = [''], and ''.includes('') is always true in JS, which hid the
    // floating chat button on every single route (bug behind ADO 4199066).
    this.hiddenRoutes = (this.resourceService.getPortalSetting('Global_Hidden_Chat_Button_Routes')?.Value?.split(',') || [])
      .map((route) => route.trim())
      .filter((route) => route.length > 0);

    combineLatest([
      this.loggedInUserSettings.$selectedPlan,
      this.loggedInUserSettings.$userClaims,
      this.asappChatInitialized$,
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([selectedPlan, user, asappChatInitialized]: [ISelectedPlan, UserClaims, boolean]) => {

      if(!selectedPlan || !user || (!asappChatInitialized)) {
        if (!this.unauthenticatedChatEnabled) {
          this.hide();
        }
        return;
      }

      const isAsappAvailable = asappChatInitialized;

      //console.log('ASAPP Available:', isAsappAvailable);
      if(isAsappAvailable) {
        this.setChatButtonVisibility(true);
        this.isChatButtonAvailable.next(true);
      } else {
        this.setChatButtonVisibility(false);
        this.isChatButtonAvailable.next(false);
      }
    });

    combineLatest([
      this.asappChatVisible$,
      this.miawChatVisible$,
      this.isChatButtonAvailable$,
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd))
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([isAsappChatVisible, isMiawChatVisible, isChatButtonAvailable, navigationEnd]: [boolean, boolean, boolean, NavigationEnd]) => {
      if(!isChatButtonAvailable) {
        return;
      }
      const hiddenRoutesLower = this.hiddenRoutes.map(route => route.trim().toLowerCase());
      const isRouteHidden = hiddenRoutesLower.some(route => navigationEnd.urlAfterRedirects.toLowerCase().includes(route));
      
      //console.log('Asapp Chat Visible:', isAsappChatVisible, 'MIAW Chat Visible:', isMiawChatVisible, 'Current Route Hidden:', isRouteHidden);

      if(isRouteHidden) {
        this.setChatButtonHidden(true);
        return;
      }

      if(!isAsappChatVisible && !isMiawChatVisible) {
        this.setChatButtonHidden(false);
      } else {
        this.setChatButtonHidden(true);
      }
    });
  }
}